import {
  ONBOARDING_OCCASION_EVENT,
  type OnboardingOccasion,
  type OnboardingOccasionDetail,
} from "@playbookai/types";

/**
 * The BROWSER half of the onboarding-occasion seam.
 *
 * The contract — the event name, the occasion vocabulary and the payload shape
 * — lives in `@playbookai/types` because three packages share it and one of
 * them (`services/*`) has no DOM. The dispatch lives HERE because it touches
 * `window`, and `@playbookai/types` is compiled without the DOM lib on purpose:
 * adding `"dom"` to that package's `lib` to make one function compile would
 * assert a browser to every backend that imports a type from it, and the next
 * `document.querySelector` would typecheck cleanly all the way into a Node
 * process.
 *
 * `packages/ui` is the right home rather than the shell: a feature package may
 * import this package (ADR-0009) and may not import the shell or a sibling
 * feature, and the tour renderer this feeds already lives next door.
 */

/**
 * Announce an occasion.
 *
 * Safe to call from anywhere: outside a browser it is a no-op rather than a
 * crash, so a server-rendered or Node-side import of the module that calls it
 * costs nothing. The check is on `dispatchEvent` rather than on `window`
 * itself, because a partial DOM shim (jsdom fragments, some SSR harnesses) can
 * define the global without the method.
 */
export function announceOnboardingOccasion(
  detail: OnboardingOccasionDetail,
): void {
  if (
    typeof window === "undefined" ||
    typeof window.dispatchEvent !== "function"
  ) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<OnboardingOccasionDetail>(ONBOARDING_OCCASION_EVENT, {
      detail,
    }),
  );
}

/**
 * Subscribe to occasions. Returns its own unsubscribe.
 *
 * Exported beside the dispatcher so the listener and the dispatcher agree on
 * the event name and the payload cast by construction — a host that wired
 * `addEventListener` itself would be one string literal away from listening to
 * nothing, silently and for ever.
 */
export function onOnboardingOccasion(
  handler: (detail: OnboardingOccasionDetail) => void,
): () => void {
  if (
    typeof window === "undefined" ||
    typeof window.addEventListener !== "function"
  ) {
    return () => {};
  }
  const listener = (event: Event): void => {
    const detail = (event as CustomEvent<OnboardingOccasionDetail>).detail;
    if (!detail || typeof detail.occasion !== "string") return;
    handler(detail);
  };
  window.addEventListener(ONBOARDING_OCCASION_EVENT, listener);
  return () => window.removeEventListener(ONBOARDING_OCCASION_EVENT, listener);
}

export {
  ONBOARDING_OCCASION_EVENT,
  type OnboardingOccasion,
  type OnboardingOccasionDetail,
};
